(function () {
  const RIO_SELECTOR = '.rio-options';
  const syncTimers = new WeakMap();
  const pendingSelections = new WeakMap();
  const syncingFromRio = new WeakSet();

  function getRioRoot(productInfo) {
    return productInfo.querySelector(RIO_SELECTOR);
  }

  function positionRioOptions(productInfo) {
    const mount = productInfo.querySelector('[data-rio-options-mount]');
    const variantSelects = mount?.querySelector('variant-selects[data-sticky="false"]');
    const rioRoot = getRioRoot(productInfo);

    if (!mount || !variantSelects || !rioRoot) return;
    if (rioRoot.nextElementSibling === variantSelects) return;

    mount.insertBefore(rioRoot, variantSelects);
  }

  function getOptionSection(rioRoot, titleEl) {
    let section = titleEl.parentElement;

    while (
      section &&
      section !== rioRoot &&
      !section.querySelector(
        '[class*="PwzrButton-root"], [class*="PwzrSelect-select"], [class*="MuiSelect-select"], [role="button"][aria-haspopup="listbox"], select'
      )
    ) {
      section = section.parentElement;
    }

    return section;
  }

  function getOptionNameForTarget(rioRoot, target) {
    let node = target;

    while (node && node !== rioRoot) {
      const titleEl = node.querySelector('.rio-product-option-title');
      if (titleEl) return titleEl.textContent.trim();
      node = node.parentElement;
    }

    return null;
  }

  function addPendingSelection(productInfo, name, value) {
    const pending = pendingSelections.get(productInfo) || {};
    pending[name] = value;
    pendingSelections.set(productInfo, pending);
  }

  function isRioButtonSelected(button) {
    const bg = button.style.backgroundColor;
    const border = button.style.borderColor;

    return (
      bg === 'rgb(0, 0, 0)' ||
      (bg === 'rgb(240, 248, 255)' && border === 'rgb(14, 29, 48)') ||
      button.getAttribute('aria-pressed') === 'true' ||
      button.classList.contains('selected')
    );
  }

  function getRioDropdownValue(section) {
    return (
      section.querySelector('[class*="PwzrSelect-select"]')?.textContent?.trim() ||
      section.querySelector('[class*="MuiSelect-select"]')?.textContent?.trim() ||
      section.querySelector('[role="button"][aria-haspopup="listbox"]')?.textContent?.trim() ||
      null
    );
  }

  function rememberClickSelection(event, productInfo) {
    const rioRoot = getRioRoot(productInfo);
    if (!rioRoot) return;

    const clickedOption = event.target.closest('[role="option"]');
    if (clickedOption) {
      const value = clickedOption.textContent?.trim();
      const listbox = clickedOption.closest('[role="listbox"]');
      const listboxId = listbox?.id;
      const trigger =
        (listboxId && rioRoot.querySelector(`[aria-controls="${listboxId}"]`)) ||
        rioRoot.querySelector('[role="button"][aria-expanded="true"][aria-haspopup="listbox"]') ||
        rioRoot.querySelector('[class*="PwzrSelect-select"][aria-expanded="true"]');
      const name = trigger ? getOptionNameForTarget(rioRoot, trigger) : null;

      if (value && name) addPendingSelection(productInfo, name, value);
      return;
    }

    if (!rioRoot.contains(event.target)) return;

    const clickedButton = event.target.closest('[class*="PwzrButton-root"]');
    if (!clickedButton || clickedButton.tagName === 'SPAN') return;

    const value = clickedButton.textContent?.trim();
    const name = getOptionNameForTarget(rioRoot, clickedButton);

    if (value && name) addPendingSelection(productInfo, name, value);
  }

  function getRioSelectedValues(rioRoot) {
    const selected = [];

    rioRoot.querySelectorAll('.rio-product-option-title').forEach((titleEl) => {
      const optionName = titleEl.textContent.trim();
      const section = getOptionSection(rioRoot, titleEl);

      if (!section) return;

      const nativeSelect = section.querySelector('select');
      if (nativeSelect?.value) {
        selected.push({ name: optionName, value: nativeSelect.value });
        return;
      }

      const dropdownValue = getRioDropdownValue(section);
      if (dropdownValue) {
        selected.push({ name: optionName, value: dropdownValue });
        return;
      }

      const buttons = section.querySelectorAll('[class*="PwzrButton-root"]');
      for (const button of buttons) {
        if (button.tagName === 'SPAN') continue;

        const label = button.textContent?.trim();
        if (!label || !isRioButtonSelected(button)) continue;

        selected.push({ name: optionName, value: label });
        break;
      }
    });

    return selected;
  }

  function getStickySelectedValues(stickySelects) {
    const selected = [];

    stickySelects.querySelectorAll('fieldset').forEach((fieldset) => {
      const name = fieldset.querySelector('legend.form__label')?.textContent?.trim();
      const checked = fieldset.querySelector('input[type="radio"]:checked');

      if (name && checked?.value) {
        selected.push({ name, value: checked.value });
      }
    });

    stickySelects.querySelectorAll('select[name^="options"]').forEach((select) => {
      const match = select.name.match(/options\[(.+)\]/);
      if (match?.[1]) {
        selected.push({ name: match[1], value: select.value });
      }
    });

    return selected;
  }

  function mirrorSelectionsToMain(productInfo, selections) {
    const mainSelects = productInfo.querySelector('variant-selects[data-sticky="false"]');
    if (!mainSelects || !selections.length) return;

    selections.forEach(({ name, value }) => {
      const mainSelect = mainSelects.querySelector(`select[name="options[${name}]"]`);
      if (mainSelect && mainSelect.value !== value) {
        mainSelect.value = value;
      }
    });
  }

  function syncRioFromVariant(productInfo, variantId) {
    const wiz = window.__productWizRioOptions;
    if (!wiz || !getRioRoot(productInfo) || !variantId) return;

    syncingFromRio.add(productInfo);
    wiz.initialSelectedVariantId = String(variantId);

    if (typeof wiz.forceUpdate === 'function') {
      wiz.forceUpdate();
    }

    window.setTimeout(() => syncingFromRio.delete(productInfo), 800);
  }

  function mirrorSelectionsToSticky(productInfo, selections) {
    const stickySelects = productInfo.querySelector('variant-selects[data-sticky="true"]');
    if (!stickySelects || !selections.length) return;

    selections.forEach(({ name, value }) => {
      const stickySelect = stickySelects.querySelector(`select[name="options[${name}]"]`);
      if (stickySelect && stickySelect.value !== value) {
        stickySelect.value = value;
      }

      stickySelects.querySelectorAll('fieldset').forEach((fieldset) => {
        const legend = fieldset.querySelector('legend.form__label');
        if (legend?.textContent.trim() !== name) return;

        const stickyRadio = fieldset.querySelector(
          `input[type="radio"][value="${CSS.escape(value)}"]`
        );
        if (stickyRadio && !stickyRadio.checked) {
          stickyRadio.checked = true;
        }
      });
    });
  }

  function applySelections(productInfo, selections) {
    const variantSelects = productInfo.querySelector('variant-selects[data-sticky="false"]');
    if (!variantSelects || !selections.length) return;

    let changedSelect = null;

    selections.forEach(({ name, value }) => {
      const themeSelect = variantSelects.querySelector(`select[name="options[${name}]"]`);
      if (!themeSelect || themeSelect.value === value) return;

      themeSelect.value = value;
      changedSelect = themeSelect;
    });

    mirrorSelectionsToSticky(productInfo, selections);

    if (!changedSelect) return;

    syncingFromRio.add(productInfo);
    changedSelect.dispatchEvent(new Event('change', { bubbles: true }));

    window.setTimeout(() => {
      syncingFromRio.delete(productInfo);

      const variantInput = productInfo.querySelector('.product-form input[name="id"]');
      const variantId = variantInput?.value;

      if (variantId) {
        document.dispatchEvent(
          new CustomEvent('variantChanged', { detail: { variantId: Number(variantId) } })
        );
      }
    }, 800);
  }

  function syncRioToThemeVariantSelects(productInfo) {
    const rioRoot = getRioRoot(productInfo);
    if (!rioRoot) return;

    const pending = pendingSelections.get(productInfo);
    if (pending && Object.keys(pending).length) {
      pendingSelections.delete(productInfo);
      applySelections(
        productInfo,
        Object.entries(pending).map(([name, value]) => ({ name, value }))
      );
      return;
    }

    applySelections(productInfo, getRioSelectedValues(rioRoot));
  }

  function scheduleSync(productInfo) {
    const existingTimer = syncTimers.get(productInfo);
    if (existingTimer) window.clearTimeout(existingTimer);

    syncTimers.set(
      productInfo,
      window.setTimeout(() => {
        syncTimers.delete(productInfo);
        syncRioToThemeVariantSelects(productInfo);
      }, 150)
    );
  }

  function hookProductWiz(productInfo) {
    const wiz = window.__productWizRioOptions;
    if (!wiz || productInfo.dataset.wizHooked === 'true') return;

    const originalOnChange = wiz.onChange;
    wiz.onChange = function (...args) {
      if (typeof originalOnChange === 'function') {
        originalOnChange.apply(this, args);
      }
      scheduleSync(productInfo);
    };

    productInfo.dataset.wizHooked = 'true';
  }

  function bindRio(productInfo) {
    if (productInfo.dataset.rioBound === 'true') return;
    productInfo.dataset.rioBound = 'true';

    productInfo.addEventListener(
      'click',
      (event) => {
        rememberClickSelection(event, productInfo);
        if (event.target.closest(RIO_SELECTOR) || event.target.closest('[role="option"]')) {
          scheduleSync(productInfo);
        }
      },
      true
    );

    productInfo.addEventListener(
      'change',
      (event) => {
        if (!event.target.closest(RIO_SELECTOR)) return;
        scheduleSync(productInfo);
      },
      true
    );

    hookProductWiz(productInfo);

    if (!productInfo.dataset.wizHooked) {
      const wizTimer = window.setInterval(() => {
        hookProductWiz(productInfo);
        if (productInfo.dataset.wizHooked) window.clearInterval(wizTimer);
      }, 300);
    }
  }

  function scheduleStickyToRioSync(productInfo) {
    const timerKey = `sticky-${productInfo.dataset.section || 'default'}`;
    const existing = syncTimers.get(timerKey);
    if (existing) window.clearTimeout(existing);

    syncTimers.set(
      timerKey,
      window.setTimeout(() => {
        syncTimers.delete(timerKey);
        const variantId = productInfo.querySelector('.product-form input[name="id"]')?.value;
        if (variantId) syncRioFromVariant(productInfo, variantId);
      }, 500)
    );
  }

  function bindStickySync(productInfo) {
    if (productInfo.dataset.stickyBound === 'true') return;
    productInfo.dataset.stickyBound = 'true';

    productInfo.addEventListener(
      'change',
      (event) => {
        const stickySelects = event.target.closest('variant-selects[data-sticky="true"]');
        if (!stickySelects || !getRioRoot(productInfo)) return;

        mirrorSelectionsToMain(productInfo, getStickySelectedValues(stickySelects));
        scheduleStickyToRioSync(productInfo);
      },
      true
    );
  }

  function bindVariantChangeListener() {
    if (bindVariantChangeListener.done || typeof subscribe !== 'function') return;

    bindVariantChangeListener.done = true;

    subscribe('variant-change', (payload) => {
      const variant = payload?.data?.variant;
      if (!variant?.id) return;

      document.querySelectorAll('product-info').forEach((productInfo) => {
        if (!getRioRoot(productInfo) || syncingFromRio.has(productInfo)) return;
        syncRioFromVariant(productInfo, variant.id);
      });
    });
  }

  function bindGlobalListboxListener() {
    if (bindGlobalListboxListener.done) return;
    bindGlobalListboxListener.done = true;

    document.addEventListener(
      'click',
      (event) => {
        const option = event.target.closest('[role="option"]');
        if (!option) return;

        document.querySelectorAll('product-info').forEach((productInfo) => {
          if (!getRioRoot(productInfo)) return;

          rememberClickSelection(event, productInfo);
          scheduleSync(productInfo);
        });
      },
      true
    );
  }

  function observeProductInfo(productInfo) {
    if (!productInfo || productInfo.dataset.rioObserved === 'true') return;

    productInfo.dataset.rioObserved = 'true';
    positionRioOptions(productInfo);
    bindRio(productInfo);
    bindStickySync(productInfo);

    const observer = new MutationObserver(() => positionRioOptions(productInfo));
    observer.observe(productInfo, { childList: true, subtree: true });
  }

  function init() {
    bindGlobalListboxListener();
    bindVariantChangeListener();
    document.querySelectorAll('product-info').forEach(observeProductInfo);
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('product-info:loaded', (event) => observeProductInfo(event.target));
  init();
})();
